'use client'

import { useEffect } from 'react'
import type { InstrumentationProvider } from '../../core/Instrumentation'
import { setUserFromAvailableIdentifiers } from '@/helpers/user.helper'

interface ObservabilityAgentProps {
  instrumentation: InstrumentationProvider
}

/**
 * Next.js component to render observability scripts.
 *
 * Receives an InstrumentationProvider and renders all scripts
 * returned by the getScripts() method using Next.js Script component.
 *
 * @param instrumentation - Observability provider that implements InstrumentationProvider
 */
export function ObservabilityAgent({
  instrumentation,
}: ObservabilityAgentProps) {
  const SET_USER_DELAY = 2000
  const scripts = instrumentation.getScripts()

  useEffect(() => {
    setUserFromAvailableIdentifiers(instrumentation)
  }, [])

  return (
    <>
      {scripts.map((script, index) => {
        if (script.type === 'inline') {
          return (
            <script
              key={`inline-${index}`}
              dangerouslySetInnerHTML={{ __html: script.content }}
              suppressHydrationWarning
            />
          )
        }

        return (
          <script
            key={`source-${index}`}
            src={script.src}
            defer={script.defer}
            crossOrigin={script.crossOrigin as 'anonymous' | 'use-credentials'}
            integrity={script.integrity}
            onLoad={() => {
              setUserFromAvailableIdentifiers(instrumentation)

              // delays an extra 2 seconds to ensure that the user identification is set correctly after the script load
              setTimeout(
                () => setUserFromAvailableIdentifiers(instrumentation),
                SET_USER_DELAY,
              )
            }}
            suppressHydrationWarning
          />
        )
      })}
    </>
  )
}
